import { createContext, useContext, useEffect, useState } from 'react';

const CACHE_NAME = 'auth';
const TOKEN_KEY = 'token';

// Cache Storage was designed for caching
// network requests with service workers,
// mainly to make PWAs work offline.
// You can give it any value you want in this case.
const FAKE_ENDPOINT = '/fake-cache-auth-endpoint';

interface PersistJwtOptions {
  /**
   * If true, writes token to storage.
   * If false, it's only written to memory.
   */
  store?: boolean;
}

type JwtContextValue = {
  jwt: string | undefined;
  loading: boolean;
  actions: {
    persistJwt: (jwt: string, options?: PersistJwtOptions) => Promise<void>;
    clearJwt: () => Promise<void>;
  };
};

let currJwt: string | undefined;

const onJwtChangeCallbacks = new Set<() => void>();

function updateJwt(jwt?: string) {
  currJwt = jwt;
  onJwtChangeCallbacks.forEach((callback) => callback());
}

const JwtContext = createContext<JwtContextValue>({
  jwt: undefined,
  loading: false,
  actions: {
    persistJwt: async () => {
      console.warn('persistJwt not implemented');
    },
    clearJwt: async () => {
      console.warn('clearJwt not implemented');
    },
  },
});

export async function readJwt() {
  if (currJwt) {
    return currJwt;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(FAKE_ENDPOINT);

    if (!response) {
      return null;
    }

    const responseBody = await response.json();
    const jwtFromStorage = responseBody[TOKEN_KEY];
    updateJwt(jwtFromStorage);

    return jwtFromStorage;
  } catch (error) {
    console.error('readJwt error:', error);
  }
  return currJwt;
}

export async function persistJwt(jwt: string, { store }: PersistJwtOptions = {}) {
  updateJwt(jwt);

  if (!store) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const responseBody = JSON.stringify({
      [TOKEN_KEY]: jwt,
    });
    const response = new Response(responseBody);
    await cache.put(FAKE_ENDPOINT, response);
  } catch (error) {
    console.error('persistJwt error:', error);
  }
}

export async function clearJwt() {
  updateJwt(undefined);
  await persistJwt('', { store: true });
}

export function useReadJwt() {
  return useContext(JwtContext);
}

export function JwtProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState<boolean>(true);
  const [jwt, setJwt] = useState<string>();

  useEffect(() => {
    const onJwtChange = () => {
      setJwt(currJwt);
    };

    onJwtChangeCallbacks.add(onJwtChange);

    return () => {
      onJwtChangeCallbacks.delete(onJwtChange);
    };
  }, []);

  useEffect(() => {
    let didCancel = false;
    (async () => {
      setLoading(true);
      currJwt = await readJwt();
      if (didCancel) return;
      if (currJwt) {
        setJwt(currJwt);
      }
      setLoading(false);
    })();

    return () => {
      didCancel = true;
    };
  }, []);

  return (
    <JwtContext.Provider
      value={{
        jwt,
        loading,
        actions: {
          persistJwt,
          clearJwt,
        },
      }}
    >
      {children}
    </JwtContext.Provider>
  );
}

export function setWaitingForSocialLogin(value = true) {
  localStorage.setItem('waitingForSocialLogin', JSON.stringify(value));
}

export function isWaitingForSocialLogin() {
  return localStorage.getItem('waitingForSocialLogin') === 'true';
}
